import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "@phosphor-icons/react";
import { api, formatEventDate } from "../lib/api";
import Seo from "../components/Seo";

const NewsList = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/news", { params: { limit: 200 } }),
      api.get("/news/categories"),
    ]).then(([n, c]) => {
      setArticles(n.data);
      setCategories(["All", ...(c.data || [])]);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (active === "All") return articles;
    return articles.filter((a) => a.category === active);
  }, [articles, active]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <main className="grain min-h-screen" data-testid="news-list-page">
      <Seo
        path="/news"
        title="Newsroom — Fintech marketing intelligence"
        description="Original reporting, market analysis, and operator interviews from across fintech marketing. Signal, not noise."
        keywords="fintech marketing news, fintech analysis, fintech operators, fintech trends, BNPL news, lending marketing news"
      />
      {/* HEADER */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 pb-12">
          <div className="term-label mb-4">/ industry news</div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <h1 className="lg:col-span-7 font-display font-black tracking-tighter text-5xl sm:text-7xl lg:text-8xl leading-[0.9]">
              Signal,<br />not noise.
            </h1>
            <p className="lg:col-span-5 text-white/70 text-lg max-w-xl">
              Original reporting, market analysis, and operator interviews from across fintech marketing. Written by the people doing the work — not press-release rewriters.
            </p>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="border-b border-cyber-line sticky top-[64px] bg-cyber-black/95 backdrop-blur-md z-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-4 flex items-center gap-2 overflow-x-auto">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`news-filter-${c}`}
              className={`font-mono-tech text-xs uppercase tracking-[0.2em] px-4 py-2 border whitespace-nowrap transition-colors duration-150 ${
                active === c
                  ? "border-white text-cyber-black bg-white"
                  : "border-cyber-line text-white/70 hover:text-white hover:border-white/50"
              }`}
            >
              {c}
            </button>
          ))}
          <span className="ml-auto font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/40 hidden md:block">
            {filtered.length.toString().padStart(2, "0")} articles
          </span>
        </div>
      </section>

      {/* CONTENT */}
      <section>
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
          {loading && <div className="font-mono-tech text-white/40">/ loading newsroom...</div>}

          {/* FEATURED */}
          {!loading && featured && (
            <Link to={`/news/${featured.slug}`} className="group block hard-border bg-cyber-surface overflow-hidden mb-10" data-testid="news-featured">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-7 aspect-[16/10] lg:aspect-auto overflow-hidden">
                  {featured.hero_image && <img src={featured.hero_image} alt={featured.title} className="w-full h-full object-cover img-grey" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                </div>
                <div className="lg:col-span-5 p-8 sm:p-12 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-cyber-green border border-cyber-green px-2 py-1">FEATURED</span>
                      <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/60">{featured.category}</span>
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-3xl sm:text-5xl mt-5 leading-[1.0] group-hover:text-white/90 transition-colors duration-150">
                      {featured.title}
                    </h2>
                    <p className="mt-5 text-white/70 text-base sm:text-lg leading-relaxed line-clamp-4">{featured.excerpt}</p>
                  </div>
                  <div className="mt-8 flex items-center justify-between">
                    <div className="font-mono-tech text-xs text-white/50">
                      {featured.author} · {formatEventDate(featured.published_at)} · <Clock size={11} className="inline align-text-bottom" /> {featured.read_time_min} min
                    </div>
                    <ArrowUpRight size={20} className="text-white group-hover:rotate-45 transition-transform duration-150" weight="bold" />
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* REST */}
          {!loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {rest.map((a) => (
                <Link key={a.id} to={`/news/${a.slug}`} className="group hard-border bg-cyber-surface overflow-hidden flex flex-col" data-testid={`news-card-${a.slug}`}>
                  {a.hero_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={a.hero_image} alt={a.title} className="w-full h-full object-cover img-grey" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-cyber-green">{a.category}</div>
                    <h3 className="font-display font-black tracking-tighter text-xl sm:text-2xl mt-3 leading-[1.05] group-hover:text-white/90 transition-colors duration-150 line-clamp-3">
                      {a.title}
                    </h3>
                    <p className="text-white/60 text-sm mt-3 line-clamp-2 flex-1">{a.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between border-t border-cyber-line pt-4">
                      <div className="font-mono-tech text-[10px] text-white/40 uppercase tracking-[0.2em]">
                        {formatEventDate(a.published_at)} · {a.read_time_min}m
                      </div>
                      <ArrowUpRight size={14} weight="bold" className="text-white group-hover:rotate-45 transition-transform duration-150" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="hard-border p-12 text-center font-mono-tech text-white/50">
              / no articles in this category yet.
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default NewsList;
